/**
 * Field Indicator State (ORCH-251, ORCH-266, ORCH-380)
 *
 * Purpose: Computes the border-color indicator state for a single Create Media
 *          Plan field row, replacing the previous icon-based approach.
 *          Semantics are confidence-driven: green = high-confidence match;
 *          yellow = AI guess below threshold; red = required + no match;
 *          null = optional left blank (no indicator shown).
 *
 *          The state also carries a `reason` (ORCH-380): the border colour alone
 *          is lossy — green covers both "Maestro matched it" and "the user typed
 *          it", and no colour separates "Maestro hasn't looked yet" from
 *          "optional, left blank". The Maestro wizard
 *          (`MaestroMappingsStepper`) derives each field's step status from the
 *          reason rather than from a rule of its own, so the plan form's borders
 *          and the wizard's chips cannot disagree about the same field.
 * Architecture: Pure function — no JSX or React dependency. Callers
 *               (TemplateField, ExtraFields) apply the returned state as a
 *               border-color override via MUI sx and a Tooltip.
 * Dependencies: ./types (FieldMatchResult)
 */
import type { FieldMatchResult } from './types';

/**
 * Minimum confidence to show a green border (mirrors backend threshold).
 * Exported so callers (e.g. MaestroMappingsStepper) share one source of truth.
 */
export const MIN_CONFIDENCE_THRESHOLD = 0.6;

/** True when a field value is considered non-empty (used by indicator state). */
export const valuePresent = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object' && 'has_files' in (v as Record<string, unknown>)) {
    return Boolean((v as { has_files?: boolean }).has_files);
  }
  return true;
};

/** Maps indicator state to MUI theme token border color. */
export const INDICATOR_BORDER: Record<'success' | 'warning' | 'error', string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

/**
 * Indicator mode (ORCH-266):
 * - 'none'            → no coloring (initial page load, before any user action)
 * - 'maestro-pending' → brief attached, Maestro's first pass hasn't returned yet.
 *                       Fields that already have a value (e.g. the brief field itself)
 *                       show their color; blank fields stay uncolored — Maestro hasn't
 *                       had a chance to say what it did or didn't find, so nothing is red.
 * - 'maestro'         → Maestro has returned real output (applied fields or a completed
 *                       pass) — full coloring, including red for required-and-blank.
 * - 'submitted'       → validate-on-submit coloring (no brief path: red only appears
 *                       after submit attempt)
 */
export type IndicatorMode = 'none' | 'maestro-pending' | 'maestro' | 'submitted';

export interface FieldIndicatorProps {
  fieldId: string;
  isRequired: boolean;
  indicatorMode: IndicatorMode;
  appliedFieldKeys: Set<string> | undefined;
  fieldResults: Record<string, FieldMatchResult> | undefined;
  /** Whether the field currently holds a non-empty value (manual entry counts). */
  hasValue?: boolean;
}

/**
 * Why a field is in the indicator state it is (ORCH-380). Exactly one reason
 * per branch of `getFieldIndicatorState`; the `state` / `tooltipText` pair is
 * a function of the reason (plus the confidence and source term it quotes).
 *
 * - `suppressed`         — indicatorMode 'none': nothing is coloured yet
 * - `awaiting-maestro`   — required and blank while 'maestro-pending': Maestro has
 *                          not reported, so the field is not flagged red yet
 * - `optional-blank`     — optional, blank, and Maestro reported nothing for it
 * - `optional-unmatched` — optional, blank, and Maestro said it could not fill it
 * - `required-missing`   — required, blank, and Maestro reported nothing for it
 * - `required-unmatched` — required, blank, and Maestro said it could not fill it
 * - `maestro-match`      — Maestro matched it at or above the confidence threshold
 * - `ai-guess`           — Maestro matched it below the threshold — needs verifying
 * - `manual`             — the value is the user's own (typed after an unmatched
 *                          verdict, or with no Maestro verdict at all)
 * - `applied`            — Maestro applied the value without a per-field verdict
 *                          (the ORCH-203 `appliedFieldKeys` path)
 */
export type FieldIndicatorReason =
  | 'suppressed'
  | 'awaiting-maestro'
  | 'optional-blank'
  | 'optional-unmatched'
  | 'required-missing'
  | 'required-unmatched'
  | 'maestro-match'
  | 'ai-guess'
  | 'manual'
  | 'applied';

export interface FieldIndicatorState {
  /**
   * Border-color signal. Maps to MUI theme tokens:
   * success → success.main (green), warning → warning.main (amber),
   * error → error.main (red), null → no border override.
   */
  state: 'success' | 'warning' | 'error' | null;
  /** Tooltip text shown on hover. Null when no context to surface. */
  tooltipText: string | null;
  /** Which branch produced the state — see `FieldIndicatorReason`. */
  reason: FieldIndicatorReason;
}

/**
 * The indicator a form shows when Maestro is not involved at all — non-Maestro
 * forms and the 'none' mode. One frozen object so callers never spell the
 * empty state by hand (and so it can be compared by identity in tests).
 */
export const NO_INDICATOR: Readonly<FieldIndicatorState> = Object.freeze({
  state: null,
  tooltipText: null,
  reason: 'suppressed',
});

const formatConfidence = (confidence: number): string => `${Math.round(confidence * 100)}%`;

/**
 * getFieldIndicatorState — maps Maestro match state to a border-color signal
 *
 * Confidence-driven semantics (ORCH-251, ORCH-266):
 * - success: matched + confidence ≥ threshold, or value present after unmatched
 * - warning: matched + confidence < threshold (AI guess — any field)
 * - error:   required field with no value and no match
 * - null:    optional field with no value (no indicator); or indicatorMode 'none';
 *            or required-and-blank while 'maestro-pending' (Maestro hasn't reported yet)
 *
 * Timing (ORCH-266): 'none' suppresses all coloring (page load). 'maestro-pending'
 * (brief attached, no Maestro output yet) colors only fields that already have a
 * value — required-and-blank stays uncolored rather than jumping straight to red,
 * since Maestro hasn't had a chance to say what it found. 'maestro' and 'submitted'
 * both apply the full coloring logic (including red for required-and-blank) — the
 * difference is only when each is activated (real Maestro output vs. submit attempt).
 *
 * Falls back to appliedFieldKeys inference for pending / missing explicit results
 * (ORCH-203 path), same as the previous icon implementation.
 *
 * A blank field is judged on its blankness, whatever Maestro reported: a matched
 * result whose value is no longer on the form (the user cleared it) reads as
 * `required-missing` / `optional-blank`, never as a match (TC-ORCH215-24). A
 * green border always means a value is actually present.
 */
export function getFieldIndicatorState({
  fieldId,
  isRequired,
  indicatorMode,
  appliedFieldKeys,
  fieldResults,
  hasValue = false,
}: FieldIndicatorProps): FieldIndicatorState {
  if (indicatorMode === 'none') return NO_INDICATOR;

  const explicit = fieldResults?.[fieldId];
  const isApplied = appliedFieldKeys?.has(fieldId) === true;

  if (!hasValue) {
    const couldNotFill = explicit?.status === 'unmatched';
    // Maestro hasn't returned anything yet (brief just attached) — don't flag
    // required-and-blank as an error until there's an actual pass to judge against.
    if (isRequired && indicatorMode !== 'maestro-pending') {
      return couldNotFill
        ? {
            state: 'error',
            tooltipText: 'Required — Maestro could not fill this field',
            reason: 'required-unmatched',
          }
        : { state: 'error', tooltipText: 'Required — not filled yet', reason: 'required-missing' };
    }
    if (isRequired) return { state: null, tooltipText: null, reason: 'awaiting-maestro' };
    // Optional + no value → neutral; yellow is reserved for AI guesses only.
    return {
      state: null,
      tooltipText: null,
      reason: couldNotFill ? 'optional-unmatched' : 'optional-blank',
    };
  }

  // Value present. Explicit ORCH-215 result decides the provenance shown;
  // 'pending' means not-yet-evaluated and falls through to the inference path.
  if (explicit && explicit.status !== 'pending') {
    if (explicit.status === 'matched') {
      const source = explicit.source_term ? ` — from "${explicit.source_term}"` : '';
      if (explicit.confidence >= MIN_CONFIDENCE_THRESHOLD) {
        return {
          state: 'success',
          tooltipText: `Filled by Maestro (${formatConfidence(explicit.confidence)} confidence)${source}`,
          reason: 'maestro-match',
        };
      }
      return {
        state: 'warning',
        tooltipText: `AI guess (${formatConfidence(explicit.confidence)} confidence)${source} — please verify`,
        reason: 'ai-guess',
      };
    }
    // unmatched but a value exists: the user fixed it by hand.
    return { state: 'success', tooltipText: 'Filled manually', reason: 'manual' };
  }

  // ORCH-203 inference fallback (older payloads / pending results).
  return isApplied
    ? { state: 'success', tooltipText: 'Filled by Maestro', reason: 'applied' }
    : { state: 'success', tooltipText: 'Filled', reason: 'manual' };
}
